import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Bredcrum from '../../common/Bredcrum';
import Loader from '../../common/Loader';
import Accordion from '../Faqs/Accordian';
import { axiosInstance, config } from '../config';
import {
  getCourseContentKey,
  COURSE_DETAIL_CONTENT,
  buildFallbackContent,
} from './courseDetailContent';
import { getCourseDaysLabel } from './courseFilterUtils';
import { getCourseSlug, findCourseBySlug } from './courseSlugs';
import {
  FallbackImage,
  getCourseImageCropStyle,
  getCourseImageObjectPosition,
  getCourseImageSrc,
} from './courseImages';

import detailBannerImg from '../../../assets/images/courses/banner_detail.png';
import '../../../assets/styles/coursesHero.css';
import '../../../assets/styles/courseDetails.css';

const CourseDetails = () => {
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { course_slug } = useParams();

  useEffect(() => {
    getCourseInfo();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course_slug]);

  function getCourseInfo() {
    setLoading(true);

    // course_slug is usually a readable slug (e.g. "3-day-automatic-intensive"),
    // matched against the full course list. If it's a plain numeric id
    // (old bookmarked links), fetch that course directly instead.
    if (/^\d+$/.test(course_slug)) {
      axiosInstance
        .get(`get-cources/${course_slug}`)
        .then((res) => {
          setCourseData(res?.data?.cource || null);
        })
        .catch((err) => console.log(err))
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    axiosInstance
      .get(`get-cources`)
      .then((res) => {
        const courses = res?.data?.cources || [];
        const match = findCourseBySlug(courses, course_slug);
        setCourseData(match);
      })
      .catch((err) => console.log(err))
      .finally(() => {
        setLoading(false);
      });
  }

  if (loading || !courseData) {
    return <Loader />;
  }

  const contentKey = getCourseContentKey(courseData);
  const content =
    COURSE_DETAIL_CONTENT[contentKey] || buildFallbackContent(courseData);
  const daysLabel = getCourseDaysLabel(courseData);
  const hasTheory = Number(courseData.theory) > 0;
  const imageSrc = getCourseImageSrc(courseData, config.imageUrl);
  const detailCropStyle = getCourseImageCropStyle(courseData);
  const imageStyle =
    detailCropStyle || { objectPosition: getCourseImageObjectPosition(courseData) };

  return (
    <>
      <section className="course-detail-hero">
        <img src={detailBannerImg} alt="" className="course-detail-hero-img" />
      </section>

      <Bredcrum
        heading={courseData.title}
        path={[
          { name: 'Courses', path: 'courses' },
          { name: courseData.title, path: `courses/${getCourseSlug(courseData)}` },
        ]}
        variant="highlighted"
      />

      <section className="course-detail-title-bar">
        <div className="auto-container">
          <span className="course-detail-hero-tag">
            {courseData.cartype} Course
          </span>
          <h1>{content.h1}</h1>
          <div className="course-detail-hero-badges">
            <span className="course-detail-badge">
              <i className="bi bi-clock-fill"></i>
              {courseData.practical} Hours Practical
            </span>
            {daysLabel && (
              <span className="course-detail-badge">
                <i className="bi bi-calendar2-week-fill"></i>
                {daysLabel}
              </span>
            )}
            <span className="course-detail-badge price">
              <i className="bi bi-currency-pound"></i>
              {courseData.price}
            </span>
          </div>
        </div>
      </section>

      <section className="course-detail-body">
        <div className="auto-container">
          <div className="course-detail-layout">
            <div className="course-detail-main">
              <div className="course-detail-section course-detail-intro">
                <span className="course-detail-eyebrow">{content.tagline}</span>
                <h2>Introduction</h2>
                {content.intro.map((para, index) => (
                  <p key={index}>{para}</p>
                ))}
              </div>

              <div className="course-detail-section">
                <h2>Why Choose This Course?</h2>
                <ul className="course-check-list">
                  {content.whyChoose.map((item, index) => (
                    <li key={index}>
                      <i className="bi bi-check-circle-fill"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="course-detail-section">
                <h2>What You'll Learn</h2>
                <div className="course-learn-grid">
                  {content.whatYoullLearn.map((item, index) => (
                    <div key={index} className="course-learn-chip">
                      <i className="bi bi-check2-square"></i>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="course-detail-section">
                <h2>Who Is This Course For?</h2>
                <ul className="course-check-list">
                  {content.whoFor.map((item, index) => (
                    <li key={index}>
                      <i className="bi bi-person-check-fill"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="course-detail-section course-detail-faqs">
                <h2>Frequently Asked Questions</h2>
                <Accordion
                  questions={content.faqs}
                  className="course-detail-accordion-question"
                />
              </div>
            </div>

            <aside className="course-detail-sidebar">
              <div className="course-summary-card">
                <div className="course-summary-image">
                  <img
                    src={imageSrc}
                    alt={courseData.title}
                    style={imageStyle}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = FallbackImage;
                    }}
                  />
                  <span className="course-summary-transmission">
                    {courseData.cartype}
                  </span>
                </div>
                <div className="course-summary-body">
                  <div className="course-summary-price">
                    <span className="symble">£</span>
                    {courseData.price}
                  </div>
                  <p className="course-summary-tagline">{content.cardShort}</p>
                  <ul className="course-summary-facts">
                    <li>
                      <i className="bi bi-clock-fill"></i>
                      {courseData.practical} Hours Practical Driving
                    </li>
                    {hasTheory && (
                      <li>
                        <i className="bi bi-book-fill"></i>
                        {courseData.theory} Hours Theory
                      </li>
                    )}
                    {daysLabel && (
                      <li>
                        <i className="bi bi-calendar2-week-fill"></i>
                        {daysLabel}
                      </li>
                    )}
                  </ul>
                  <a
                    href={`${config.appUrl}book-a-lesson`}
                    className="course-summary-cta"
                  >
                    Book Now
                    <i className="bi bi-arrow-right"></i>
                  </a>
                  <div className="course-summary-phone">
                    <i className="bi bi-telephone-fill"></i>
                    <div>
                      <span>Call to book</span>
                      <a href="tel:01162102688">0116 210 2688</a>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="course-detail-cta">
        <div className="auto-container">
          <h2>{content.cta.heading}</h2>
          <p>{content.cta.text}</p>
          <a
            href={`${config.appUrl}book-a-lesson`}
            className="course-detail-cta-btn"
          >
            {content.cta.button}
            <i className="bi bi-arrow-right"></i>
          </a>
        </div>
      </section>
    </>
  );
};

export default CourseDetails;
